import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import UpdateIcon from "@mui/icons-material/Update";
// import FilterListIcon from '@mui/icons-material/FilterList'
import PageService from "../../../../services/PageService";
import { socketEventEmitter } from "../../../../socket";
import { useProject } from "../../../../context/ProjectContext";
import { useNotification } from "../../../../context/NotificationContext";
import TestCasesEmpty from "./TestCasesEmpty";
import TestCasesCharts from "./TestCasesCharts";
import StatsSummary from "../../../StatsSummary";
import SortingIcon from "../../../SortingIcon";
import TestCasesListItem from "./TestCasesListItem";
import SearchBar from "./SearchBar";
import PaginationComponent from "../../../PaginationComponent";
import DeleteTestCasesConfirmationModal from "../../../modals/DeleteTestCasesConfirmationModal";
import GenerateTestSettingsModal from "../../../modals/GenerateTestSettingsModal";
import { sortTableItems } from "../../../../utils/sortTableItems";
import { shouldShowTestGenerationDebug } from "../../../../utils/addExternalScripts"; // added
import { formatDate } from "../../../../utils/formatDate";
import {
  formatTokenCount,
  formatUsdCost,
  getDiscoveryDebugSummary,
} from "../../../../utils/discoveryDebug";

const TestCasesTab = ({
  pageData,
  isGenerateTestsInProgress,
  isScanLoading,
}) => {
  const navigate = useNavigate();
  const { selectedProject } = useProject();
  const showNotification = useNotification();

  const [allTestCases, setAllTestCases] = useState([]);
  const [draftTestCases, setDraftTestCases] = useState([]);
  const [testCasesTotal, setTestCasesTotal] = useState(0);
  const [checkedTestCases, setCheckedTestCases] = useState([]);
  const [testCasesToDelete, setTestCasesToDelete] = useState([]);
  const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });
  const [tabToggler, setTabToggler] = useState(false);
  const [page, setPage] = useState(1);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState("");
  // const [filtersOpen, setFiltersOpen] = useState(false)
  const [isUnsupportedForTestCases, setIsUnsupportedForTestCases] =
    useState(false);
  const [generationDebug, setGenerationDebug] = useState(null); // init as null
  const [showDebug, setShowDebug] = useState(false); // added
  const [generateTestSettingsModalOpen, setGenerateTestSettingsModalOpen] =
    useState(false);
  const [isRegenerateMode, setIsRegenerateMode] = useState(false);

  const testCasesPerPage = 10;
  const matchesSearch = (testCase, query = "") => {
    const term = (query || "").toLowerCase();
    if (!term) return true;
    return (
      (testCase.name || "").toLowerCase().includes(term) ||
      (testCase.public_id || testCase.publicId || "")
        .toLowerCase()
        .includes(term)
    );
  };

  useEffect(() => {
    // decide if debug accordion should be shown (dev only)
    (async () => {
      try {
        const allowed = await shouldShowTestGenerationDebug();
        setShowDebug(allowed);
      } catch {
        setShowDebug(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (
      pageData &&
      selectedProject?.id &&
      pageData.page.project_id !== selectedProject.id
    ) {
      navigate(`/pages`);
    }

    console.log("pageData:", pageData);

    if (
      pageData?.page.page_types &&
      pageData.page.page_types[0].type === "Unknown"
    ) {
      setIsUnsupportedForTestCases(true);
    }
    console.log("test cases tab use effect");

    const getAllTestCases = async () => {
      try {
        const testCasesData = await PageService.getAllTestCasesByPageId(
          pageData.page.id,
        );
        setAllTestCases(testCasesData.testCases);
        setTestCasesTotal(testCasesData.total);
        setDraftTestCases(
          testCasesData.testCases
            .filter((testCase) => testCase.status === "Draft")
            .map((testCase) => testCase.id),
        );
        // clear debug if there are no test cases after refresh
        if (testCasesData?.discovery_debug) {
          console.log("Discovery debug data:", testCasesData.discovery_debug);
          setGenerationDebug(testCasesData.discovery_debug);
        }
      } catch (error) {
        console.error("Error fetching test cases:", error);
      }
    };

    if (pageData?.page?.id) {
      getAllTestCases();
    }

    const handleTaskCompleted = async (data) => {
      console.log("task completed!");
      // console.log('(Test Cases Tab) Task completed:', data)
      if (
        data.pageId === pageData?.page?.id &&
        (data.type === "Test Case" || data.type === "Discover Test Cases")
      ) {
        setTabToggler(!tabToggler);
      }
      // capture discovery debug payload if sent
      if (data.type === "Discover Test Cases" && data.result?.discovery_debug) {
        setGenerationDebug(data.result.discovery_debug);
      }
    };

    const handleTaskFailed = async (data) => {
      console.log("(Test Cases Tab) Task failed:", data);
      console.log("pageData:", pageData);
      if (
        data.pageId === pageData?.page?.id &&
        (data.type === "Test Case" || data.type === "Test Case Discovery")
      ) {
        setTabToggler(!tabToggler);
        if (data?.error === "This type of page isn't supported yet.") {
          console.log("set not supported");
          setIsUnsupportedForTestCases(true);
        }
      }
      // capture discovery debug payload on failure as well (if provided)
      if (data.type === "Test Case Discovery" && data.discovery_debug) {
        setGenerationDebug(data.discovery_debug);
      }
    };

    socketEventEmitter.on("taskCompleted", handleTaskCompleted);
    socketEventEmitter.on("taskFailed", handleTaskFailed);

    return () => {
      socketEventEmitter.off("taskCompleted", handleTaskCompleted);
      socketEventEmitter.off("taskFailed", handleTaskFailed);
    };
  }, [selectedProject?.id, pageData, tabToggler, navigate]);

  // also reactively clear when total drops to zero (e.g., after deletion)
  useEffect(() => {
    if (testCasesTotal === 0) {
      setGenerationDebug(null);
    }
  }, [testCasesTotal]);

  const handleRunTestCases = async () => {
    try {
      await PageService.runTestCases(checkedTestCases);
      setCheckedTestCases([]);
      setIsHeaderCheckboxChecked(false);
    } catch (error) {
      console.log(error);
    }
    setTabToggler(!tabToggler);
  };

  const handleGenerateTestCases = () => {
    setIsRegenerateMode(false);
    setGenerateTestSettingsModalOpen(true);
  };

  const handleRunGenerateTest = async (context) => {
    try {
      await PageService.discoverTestCasesByPageId(pageData.page.id, context);
    } catch (error) {
      console.log(error);
    }
  };

  const handleRegenerateTestCases = () => {
    setIsRegenerateMode(true);
    setGenerateTestSettingsModalOpen(true);
  };

  const handleRunRegenerateTest = async (context) => {
    const allTestCasesIds = allTestCases.map((tc) => tc.id);
    showNotification(
      "Test cases regenerating has started",
      "info",
      "Learn more",
      navigate,
    );
    try {
      await PageService.regenerateTestCasesMultipleByIds(
        allTestCasesIds,
        context,
      );
    } catch (error) {
      console.log(error);
    } finally {
      setCheckedTestCases([]);
      setIsHeaderCheckboxChecked(false);
    }
  };

  const handleRegenerateSelectedTestCases = async () => {
    showNotification(
      "Test cases regenerating has started",
      "info",
      "Learn more",
      navigate,
    );
    try {
      await PageService.regenerateTestCasesMultipleByIds(checkedTestCases);
      setCheckedTestCases([]);
      setIsHeaderCheckboxChecked(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleOpenDeleteDialog = (testCaseID) => {
    if (testCaseID) {
      setTestCasesToDelete([testCaseID]);
    } else {
      setTestCasesToDelete(checkedTestCases);
    }
    setDeleteOpen(true);
  };

  // Simple details accordion rendering Test generation debug
  const renderGenerationDebug = () => {
    if (!generationDebug) return null;
    const { video_url, aws_tracing_url, llm_logs = [] } = generationDebug;
    const debugSummary = getDiscoveryDebugSummary(generationDebug);
    return (
      <details style={{ marginBottom: 16 }}>
        <summary style={{ cursor: "pointer", fontWeight: 600 }}>
          Test generation debug
        </summary>
        <div style={{ padding: 12 }}>
          {debugSummary ? (
            <div
              style={{
                marginBottom: 12,
                padding: 12,
                background: "#f7f9fc",
                border: "1px solid #e3e8ef",
                borderRadius: 8,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Gemini cost
              </div>
              <div>
                Total: {formatUsdCost(debugSummary.totalUsd)}{" "}
                {debugSummary.currency}
              </div>
              <div>
                Tokens: {formatTokenCount(debugSummary.inputTokens)} in /{" "}
                {formatTokenCount(debugSummary.outputTokens)} out
              </div>
              <div>
                Priced calls: {formatTokenCount(debugSummary.pricedCalls)}
              </div>
            </div>
          ) : null}
          {video_url ? (
            <div style={{ marginBottom: 12 }}>
              <div>Discovery video:</div>
              <video
                src={video_url}
                controls
                style={{ width: "100%", maxWidth: 640 }}
              />
            </div>
          ) : null}
          {aws_tracing_url ? (
            <div style={{ marginBottom: 12 }}>
              <a href={aws_tracing_url} target="_blank" rel="noreferrer">
                Open trace report
              </a>
            </div>
          ) : null}
          {llm_logs.length ? (
            <div style={{ overflowX: "auto" }}>
              <div style={{ fontWeight: 600, margin: "8px 0" }}>LLM I/O</div>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: "left",
                        borderBottom: "1px solid #eee",
                        padding: 6,
                      }}
                    >
                      #
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        borderBottom: "1px solid #eee",
                        padding: 6,
                      }}
                    >
                      route
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        borderBottom: "1px solid #eee",
                        padding: 6,
                      }}
                    >
                      cost (usd)
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        borderBottom: "1px solid #eee",
                        padding: 6,
                      }}
                    >
                      request json
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        borderBottom: "1px solid #eee",
                        padding: 6,
                      }}
                    >
                      request ts
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        borderBottom: "1px solid #eee",
                        padding: 6,
                      }}
                    >
                      response json
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        borderBottom: "1px solid #eee",
                        padding: 6,
                      }}
                    >
                      response ts
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        borderBottom: "1px solid #eee",
                        padding: 6,
                      }}
                    >
                      page link
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        borderBottom: "1px solid #eee",
                        padding: 6,
                      }}
                    >
                      image
                    </th>{" "}
                    {/* added */}
                  </tr>
                </thead>
                <tbody>
                  {llm_logs.map((row, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: 6 }}>{idx + 1}</td>
                      <td
                        style={{
                          padding: 6,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.route}
                      </td>
                      <td style={{ padding: 6 }}>
                        {formatUsdCost(row.llm_cost_usd)}
                      </td>
                      <td style={{ padding: 6 }}>
                        {row.request_json ? (
                          <a
                            href={row.request_json}
                            target="_blank"
                            rel="noreferrer"
                          >
                            request_json
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td style={{ padding: 6 }}>
                        {row.request_timestamp
                          ? formatDate(row.request_timestamp)
                          : "-"}
                      </td>
                      <td style={{ padding: 6 }}>
                        {row.response_json ? (
                          <a
                            href={row.response_json}
                            target="_blank"
                            rel="noreferrer"
                          >
                            response_json
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td style={{ padding: 6 }}>
                        {row.response_timestamp
                          ? formatDate(row.response_timestamp)
                          : "-"}
                      </td>
                      <td style={{ padding: 6 }}>
                        {row.page_link ? (
                          <a
                            href={row.page_link}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {row.page_link}
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td style={{ padding: 6 }}>
                        {row.image ? (
                          <a href={row.image} target="_blank" rel="noreferrer">
                            screenshot
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>{" "}
                      {/* added */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </details>
    );
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleCheckboxChange = (id) => {
    setCheckedTestCases((prevCheckedTestCases) => {
      if (prevCheckedTestCases.includes(id)) {
        return prevCheckedTestCases.filter((testCaseId) => testCaseId !== id);
      } else {
        return [...prevCheckedTestCases, id];
      }
    });
  };

  const handleSortChange = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handleSearch = (event, newValue) => {
    if (!newValue) {
      setSelectedPage("");
      return;
    }
    setSelectedPage(newValue?.name || "");
    setPage(1);
  };

  // const handleFiltersOpen = () => {
  //   setFiltersOpen(!filtersOpen)
  // }

  const sortedTestCases = sortTableItems(allTestCases, sortConfig);

  const filteredTestCases = selectedPage
    ? sortedTestCases.filter((testCase) =>
        matchesSearch(testCase, selectedPage),
      )
    : sortedTestCases;

  const paginatedTestCases = filteredTestCases.slice(
    (page - 1) * testCasesPerPage,
    page * testCasesPerPage,
  );

  const totalPages = Math.ceil(filteredTestCases.length / testCasesPerPage);

  // sync header checkbox with current page selection
  useEffect(() => {
    const currentIds = paginatedTestCases.map((tc) => tc.id);
    const allSelected =
      currentIds.length > 0 &&
      currentIds.every((id) => checkedTestCases.includes(id));
    setIsHeaderCheckboxChecked(allSelected);
  }, [paginatedTestCases, checkedTestCases]);

  const handleHeaderCheckboxToggle = () => {
    const currentIds = paginatedTestCases.map((tc) => tc.id);
    const allSelected =
      currentIds.length > 0 &&
      currentIds.every((id) => checkedTestCases.includes(id));
    if (allSelected) {
      setCheckedTestCases((prev) =>
        prev.filter((id) => !currentIds.includes(id)),
      );
    } else {
      setCheckedTestCases((prev) => [...new Set([...prev, ...currentIds])]);
    }
  };

  const isHeaderIndeterminate = (() => {
    const currentIds = paginatedTestCases.map((tc) => tc.id);
    const selectedOnPage = currentIds.filter((id) =>
      checkedTestCases.includes(id),
    );
    return (
      selectedOnPage.length > 0 && selectedOnPage.length < currentIds.length
    );
  })();

  return (
    <Box className="testCasesTabContentContainer">
      {showDebug ? renderGenerationDebug() : null}
      {/* ensure the debug table shows on load only when test cases exist */}
      {pageData && testCasesTotal ? (
        <Box className="testCasesTabContent">
          <Box className="testCasesTabDetails">
            <Box className="testCasesTabDetailsTitleContainer">
              <Typography className="testCasesTabDetailsTitle">
                Details
              </Typography>
              {isGenerateTestsInProgress ? (
                <LoadingButton
                  loading
                  className="testCasesTabDetailsRegenerateButton"
                  variant="contained"
                  color="secondary"
                  loadingIndicator={
                    <CircularProgress color="white" size={16} />
                  }
                />
              ) : (
                <Button
                  className="testCasesTabDetailsRegenerateButton"
                  onClick={handleRegenerateTestCases}
                  variant="contained"
                  color="secondary"
                  disabled={isScanLoading}
                >
                  REGENERATE TESTS
                </Button>
              )}
            </Box>
            <TestCasesCharts
              testCasesData={allTestCases}
              testCasesTotal={testCasesTotal}
              draftTestCases={draftTestCases}
            />
          </Box>
          <Divider className="testCasesTabDivider" />
          <Box className="testCasesTabStatsSection">
            {/* <StatsSummary title="Total Runs" value={testCasesTotal} color="primary" /> */}
            <StatsSummary
              title="Total Test Cases"
              value={testCasesTotal}
              color="primary"
            />
            {/* <StatsSummary title="Total Run Time" value={'0 minutes'} color="primary" />
            <StatsSummary title="Total Logged Time" value={'0 minutes'} color="primary" />
            <StatsSummary title="Runs Not Tracked" value={1} color="primary" /> */}
          </Box>
          <Box className="testCasesTabTableActionsContainer">
            <>
              <Box className="testCasesListSettingsContainer">
                <Box className="testCasesListItemSettings">
                  {checkedTestCases.length ? (
                    <>
                      <Button
                        id="runSelectedTestCasesButton"
                        className="runSelectedButton"
                        variant="contained"
                        color="secondary"
                        onClick={handleRunTestCases}
                      >
                        <PlayCircleOutlineOutlinedIcon
                          className="runSelectedButtonIcon"
                          color="#fff"
                        />
                        RUN SELECTED
                      </Button>
                      <Button
                        id="regenerateSelectedTestCasesButton"
                        className="regenerateButton"
                        variant="contained"
                        color="secondary"
                        onClick={handleRegenerateSelectedTestCases}
                        disabled={isGenerateTestsInProgress}
                      >
                        <UpdateIcon
                          className="regenerateButtonIcon"
                          color="#fff"
                        />
                        REGENERATE SELECTED
                      </Button>
                      <Button
                        id="deleteTestCasesButton"
                        className="deleteButton"
                        variant="outlined"
                        color="error"
                        onClick={() => handleOpenDeleteDialog()}
                      >
                        <DeleteOutlineOutlinedIcon
                          className="deleteButtonIcon"
                          color="error"
                        />
                        DELETE
                      </Button>
                    </>
                  ) : null}
                </Box>
                <Box className="testCasesListCommonSettings">
                  {/* <Box className='testCasesListFilters'>
                    <Button
                      className='testCasesListFiltersTitleContainer'
                      variant="text"
                      color="secondary"
                      onClick={handleFiltersOpen}
                    >
                      <FilterListIcon className='testCasesListFiltersTitleIcon' color='secondary'/>
                      <Typography className='testCasesListFiltersTitleText'>Filters</Typography>
                    </Button>
                  </Box> */}
                  <SearchBar
                    testCasesList={allTestCases}
                    handleSearch={handleSearch}
                  />
                </Box>
              </Box>
            </>
          </Box>
          <TableContainer className="testCasesTableContainer" component={Paper}>
            <Table className="testCasesTable" stickyHeader>
              <TableHead className="testCasesTableHeader">
                <TableRow>
                  <TableCell
                    className="testCasesTableHeaderCheckboxCell"
                    padding="checkbox"
                    sx={{ width: 28, minWidth: 28 }}
                  >
                    <Checkbox
                      size="small"
                      checked={isHeaderCheckboxChecked}
                      indeterminate={isHeaderIndeterminate}
                      onChange={handleHeaderCheckboxToggle}
                    />
                  </TableCell>
                  <TableCell
                    className="testCasesTableHeaderCell testCaseId"
                    onClick={() => handleSortChange("public_id")}
                  >
                    <Box className="testCasesTableHeaderCellContent">
                      <SortingIcon
                        sortConfig={sortConfig}
                        currentKey={"public_id"}
                      />
                      Test Case ID
                    </Box>
                  </TableCell>
                  <TableCell
                    className="testCasesTableHeaderCell testCaseName"
                    onClick={() => handleSortChange("name")}
                  >
                    <Box className="testCasesTableHeaderCellContent">
                      <SortingIcon
                        sortConfig={sortConfig}
                        currentKey={"name"}
                      />
                      Test Case Name
                    </Box>
                  </TableCell>
                  <TableCell
                    className="testCasesTableHeaderCell"
                    onClick={() => handleSortChange("created_at")}
                  >
                    <Box className="testCasesTableHeaderCellContent">
                      <SortingIcon
                        sortConfig={sortConfig}
                        currentKey={"created_at"}
                      />
                      Created
                    </Box>
                  </TableCell>
                  <TableCell
                    className="testCasesTableHeaderCell"
                    onClick={() => handleSortChange("regenerated_at")}
                  >
                    <Box className="testCasesTableHeaderCellContent">
                      <SortingIcon
                        sortConfig={sortConfig}
                        currentKey={"regenerated_at"}
                      />
                      Last Updated
                    </Box>
                  </TableCell>
                  <TableCell
                    className="testCasesTableHeaderCell"
                    onClick={() => handleSortChange("created_by")}
                  >
                    <Box className="testCasesTableHeaderCellContent">
                      <SortingIcon
                        sortConfig={sortConfig}
                        currentKey={"created_by"}
                      />
                      Created by
                    </Box>
                  </TableCell>
                  <TableCell
                    className="testCasesTableHeaderCell"
                    onClick={() => handleSortChange("status")}
                  >
                    <Box className="testCasesTableHeaderCellContent">
                      <SortingIcon
                        sortConfig={sortConfig}
                        currentKey={"status"}
                      />
                      Status
                    </Box>
                  </TableCell>
                  <TableCell className="testCasesTableHeaderCellContent testCasesActions">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTestCases.length ? (
                  paginatedTestCases.map((testCase) => (
                    <TestCasesListItem
                      key={testCase.id}
                      testCase={testCase}
                      checkedTestCases={checkedTestCases}
                      handleCheckboxChange={handleCheckboxChange}
                      handleOpenDeleteDialog={handleOpenDeleteDialog}
                      tabToggler={tabToggler}
                      setTabToggler={setTabToggler}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="testCasesTableEmptyByFilters"
                    >
                      <Typography
                        variant="body1"
                        className="testCasesTableEmptyByFiltersText"
                      >
                        No data available. Click on &apos;Generate Test
                        Cases&apos; button.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box mr={2}>
            <PaginationComponent
              page={page}
              totalItems={filteredTestCases.length}
              itemsPerPage={testCasesPerPage}
              count={totalPages}
              handlePageChange={handlePageChange}
            />
          </Box>
        </Box>
      ) : (
        <TestCasesEmpty
          isScanLoading={isScanLoading}
          isGenerateLoading={isGenerateTestsInProgress}
          handleGenerateTestCases={handleGenerateTestCases}
          noticeText={
            isUnsupportedForTestCases
              ? "This page type isn’t supported for test generation right now. If the page has changed, you can try again."
              : undefined
          }
        />
      )}
      <DeleteTestCasesConfirmationModal
        deleteOpen={deleteOpen}
        setDeleteOpen={setDeleteOpen}
        tabToggler={tabToggler}
        setTabToggler={setTabToggler}
        testCasesToDelete={testCasesToDelete}
        setCheckedTestCases={setCheckedTestCases}
      />
      <GenerateTestSettingsModal
        open={generateTestSettingsModalOpen}
        setOpen={(open) => {
          setGenerateTestSettingsModalOpen(open);
          if (!open) {
            setIsRegenerateMode(false);
          }
        }}
        onRun={
          isRegenerateMode ? handleRunRegenerateTest : handleRunGenerateTest
        }
      />
    </Box>
  );
};

export default TestCasesTab;
